/**
 * The family's record: who is in it and how to reach them.
 *
 * This is what My Family used to be mixed in with grades and enrollment. It is
 * reference material — looked up occasionally, not read daily — so it lives on
 * its own page rather than above the things a parent checks every week.
 *
 * A parent can ask for changes here, but cannot make them: the school owns this
 * record, so an edit is a request that an administrator reviews item by item.
 */
import { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { readFragment } from 'gql.tada';
import type { AppLayoutContext } from '~/types';
import { useViewAsFamily } from '~/hooks/useViewAsFamily';
import { useMyFamilyQuery } from '../MyFamily/useMyFamilyQuery';
import { ParentFamilyInfo, type HouseholdSection } from '../MyFamily/ParentFamilyInfo';
import { GradeEmailPreference } from '../MyFamily/GradeEmailPreference';
import { HouseholdChangeRequestFragment } from './HouseholdChangeRequestFragment';
import { PendingChangesBanner } from './PendingChangesBanner';
import { EditHouseholdDialog } from './EditHouseholdDialog';
import { useAcknowledgeHouseholdChangeRequest } from './useAcknowledgeHouseholdChangeRequest';

export default function ParentHousehold() {
  const { school, roleContext } = useOutletContext<AppLayoutContext>();
  const selectedYear = roleContext?.selectedSchoolYear;
  // An admin viewing as this family sees the record, but does not ask the
  // school for changes on the family's behalf, and does not acknowledge the
  // family's banners for them.
  const { familyId: viewAsFamilyId, readOnly } = useViewAsFamily(school?.slug);
  const { data, loading, error, refetch } = useMyFamilyQuery(school?.id, selectedYear?.id, viewAsFamilyId ?? undefined);
  const [editingSection, setEditingSection] = useState<HouseholdSection | null>(null);
  const [acknowledge] = useAcknowledgeHouseholdChangeRequest();

  // The reviewed-request banner is shown once. Acknowledging it as soon as the
  // page renders leaves it on screen for this visit — the query is not refetched
  // — and gone the next time the parent comes back. The ref keeps a re-render
  // from sending it twice.
  const reviewed = readFragment(
    HouseholdChangeRequestFragment,
    data?.myFamily?.lastReviewedHouseholdChangeRequest,
  );
  const reviewedId = reviewed?.id;
  const acknowledgedRef = useRef<string | null>(null);
  useEffect(() => {
    if (readOnly) return;
    if (!reviewedId || acknowledgedRef.current === reviewedId) return;
    acknowledgedRef.current = reviewedId;
    acknowledge({ variables: { requestId: reviewedId } }).catch(() => {
      // A failed acknowledgement only means the banner shows again next visit.
      acknowledgedRef.current = null;
    });
  }, [reviewedId, acknowledge, readOnly]);

  if (loading) return <div className="p-6 text-muted-foreground">Loading…</div>;
  if (error) return <div className="p-6 text-destructive">Something went wrong loading your family.</div>;

  const family = data?.myFamily;
  if (!family) {
    return (
      <div className="p-6">
        <h1 className="text-3xl font-bold">Household</h1>
        <p className="mt-4 text-muted-foreground">
          No family is associated with your account for this school year yet.
        </p>
      </div>
    );
  }

  const pending = readFragment(HouseholdChangeRequestFragment, family.pendingHouseholdChangeRequest);
  const lastReviewed = reviewed;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-3xl font-bold">{family.name}</h1>
        <p className="text-muted-foreground">Contacts and details the school has on file.</p>
      </div>

      <PendingChangesBanner pending={pending} lastReviewed={lastReviewed} />

      <ParentFamilyInfo
        family={family}
        onEditSection={pending ? undefined : setEditingSection}
        editDisabled={readOnly}
      />

      {data?.currentUser && !readOnly && (
        <div className="border-t pt-6">
          <h2 className="mb-3 text-lg font-semibold">Notifications</h2>
          <GradeEmailPreference optedIn={data.currentUser.gradePostedEmailOptIn} />
        </div>
      )}

      {editingSection && (
        <EditHouseholdDialog
          section={editingSection}
          open
          onOpenChange={(open) => {
            if (!open) setEditingSection(null);
          }}
          family={family}
          onSubmitted={() => void refetch()}
        />
      )}
    </div>
  );
}
